import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Plus,
  Minus,
  Mountain,
  HardHat,
  Briefcase,
  Scale,
  Leaf,
  FileSearch,
  Handshake,
  Users,
  ShieldCheck,
  Layers,
  Network,
} from 'lucide-react';
import { colors } from '../styles/colors';
import { useFillout } from '../hooks/useFillout';

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

// Content integrated from the approved manuscript (Phase 3).
const professionals = [
  {
    id: 'geological-consultants', Icon: Mountain, title: 'Geological Consultants',
    role: 'Geologists define the resource — establishing the foundational understanding of the mineral asset on which a project\u2019s viability depends.',
    support: 'Their technical reports and resource validation provide the geological confidence that every downstream decision, and every funding discussion, is built upon.',
    arizore: 'Arizore\u2019s role begins once geological expertise has created that confidence — connecting validated resources with institutional capital, never substituting for independent geological work.',
    value: 'Credible, defensible geological data that transforms exploration potential into investable opportunity.',
    engagements: 'Technical reporting · Resource estimation · Mineral resource validation',
  },
  {
    id: 'mining-engineers', Icon: HardHat, title: 'Mining Engineers',
    role: 'Engineers assess technical feasibility — determining whether a resource can be developed practically, safely and economically.',
    support: 'Their assessments give investors and funding partners the confidence that a project can be executed as planned.',
    arizore: 'Arizore works alongside engineering assessments, connecting technically sound projects to institutional funding pathways rather than duplicating operational expertise.',
    value: 'Engineering confidence that turns a defined resource into a credible, executable project.',
    engagements: 'Feasibility assessment · Technical review · Development planning',
  },
  {
    id: 'corporate-advisers', Icon: Briefcase, title: 'Corporate Advisers',
    role: 'Corporate advisers prepare projects for investment — structuring and presenting them so institutional capital can engage with clarity.',
    support: 'They position projects to be understood and evaluated by investors, helping create the confidence that enables capital to participate.',
    arizore: 'Arizore complements advisory work by extending prepared projects into a structured, reserve-backed funding model.',
    value: 'Investment-ready projects, well positioned for institutional engagement.',
    engagements: 'Investment readiness · Corporate structuring · Capital preparation',
  },
  {
    id: 'legal-professionals', Icon: Scale, title: 'Legal Professionals',
    role: 'Legal professionals establish governance and transaction certainty — the frameworks that make responsible funding possible.',
    support: 'Robust legal structures give investors and lenders the assurance required to commit capital with confidence.',
    arizore: 'Arizore operates within established legal frameworks, connecting projects with sound governance to institutional capital.',
    value: 'Governance certainty that protects every participant and underpins trusted transactions.',
    engagements: 'Legal structuring · Governance frameworks · Transaction documentation',
  },
  {
    id: 'esg-specialists', Icon: Leaf, title: 'ESG Specialists',
    role: 'Environmental and ESG specialists help ensure responsible development across a project\u2019s lifecycle.',
    support: 'They embed the environmental, social and governance standards that communities and institutional capital increasingly expect.',
    arizore: 'Arizore recognises responsible development as integral to durable projects, connecting well-governed resources with capital that shares those standards.',
    value: 'Responsibly developed projects aligned with modern institutional expectations.',
    engagements: 'Environmental assessment · ESG advisory · Responsible development',
  },
  {
    id: 'technical-due-diligence', Icon: FileSearch, title: 'Technical Due Diligence',
    role: 'Independent specialists verify each stage of a project\u2019s development.',
    support: 'Independent assessment builds the foundation of trust that investors, lenders and funding partners rely upon.',
    arizore: 'Arizore leverages the confidence established through independent verification, connecting verified resources with institutional capital.',
    value: 'Independent assurance that strengthens confidence throughout the ecosystem.',
    engagements: 'Independent assessment · Technical verification · Project validation',
  },
  {
    id: 'strategic-introducers', Icon: Handshake, title: 'Strategic Industry Introducers',
    role: 'Trusted introducers bring quality opportunities together through long-standing industry relationships.',
    support: 'Their networks and judgement connect viable projects with the right expertise and stakeholders.',
    arizore: 'Arizore works alongside introducers to connect verified resources with institutional capital, strengthening existing relationships rather than replacing them.',
    value: 'Stronger connections and better-matched, higher-quality opportunities.',
    engagements: 'Industry introductions · Relationship building · Ecosystem connectivity',
  },
];

const pillars = [
  { Icon: Users, title: 'Collaboration', body: 'Arizore works alongside trusted professionals throughout the mining lifecycle.' },
  { Icon: ShieldCheck, title: 'Expertise', body: 'Independent technical specialists remain the foundation of credible resource verification.' },
  { Icon: Network, title: 'Connectivity', body: 'Arizore connects verified resources with institutional capital.' },
  { Icon: Layers, title: 'Trust', body: 'Long-term relationships and professional integrity underpin successful outcomes.' },
];

// §4 relationship map nodes — 5 participants at equal 72° angular spacing (symmetric about vertical).
const ecoNodes = [
  { label: 'Technical Expertise', left: 50, top: 12, desc: 'Geology and engineering establish what a resource is and whether it can be responsibly developed.' },
  { label: 'Project Verification', left: 86.1, top: 38.3, desc: 'Independent assessment and due diligence build the confidence funding partners can trust.' },
  { label: 'Institutional Capital', left: 72.3, top: 80.7, desc: 'Long-term capital participates once technical and commercial confidence is established.' },
  { label: 'Industry Professionals', left: 27.7, top: 80.7, desc: 'Advisory expertise and trusted relationships bring quality opportunities together.' },
  { label: 'Legal Structuring', left: 13.9, top: 38.3, desc: 'Governance and transaction certainty give institutions the assurance to participate.' },
];

const IndustryProfessionalsPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }} data-testid="industry-professionals-page">
      {/* §HERO — editorial introduction, no CTA */}
      <section className="relative overflow-hidden" style={{ minHeight: '82vh', background: 'linear-gradient(180deg, #000000 0%, #03090C 60%, #07141A 100%)' }}>
        <img
          src="/industry-professionals-hero.webp"
          alt=""
          aria-hidden="true"
          data-testid="ip-hero-image"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{ objectPosition: 'center center' }}
          loading="eager"
          decoding="async"
        />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.6) 72%, rgba(5,5,5,0.97) 100%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(46% 42% at 50% 48%, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0) 75%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 65% at 50% 50%, rgba(0,128,128,0.08) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[82vh] pt-32 pb-20 sm:pt-36 sm:pb-24">
          <div className={`transition-all duration-1000 ease-out transform ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}>
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal }}>
                <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
                The Wider Ecosystem
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-[-0.02em] mb-6 max-w-4xl mx-auto" style={{ color: colors.white, fontWeight: 700 }}>
              INDUSTRY PROFESSIONALS
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl leading-snug max-w-2xl mx-auto mb-6" style={{ color: colors.white, fontWeight: 500 }}>
              The expertise behind every successful funding journey.
            </p>
            <p className="text-base sm:text-lg leading-[1.6] max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Every successful mining project is built on the expertise, integrity and judgement of industry professionals — the specialists who transform mineral resources into investable opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* §1 THE PROFESSIONAL ECOSYSTEM */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/home-bg-grid.webp)', backgroundSize: 'cover', backgroundPosition: 'center 20%', opacity: 0.09 }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="A Collaborative Industry"
            title="MINING IS A COLLABORATIVE INDUSTRY."
            sub="No successful mining project is delivered by a single organisation. Behind every producing mine is a network of specialists whose expertise provides the technical confidence, governance and commercial structure required for responsible resource development."
          />

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 max-w-3xl mx-auto mb-12">
            {[
              'Geologists define the resource.',
              'Engineers assess technical feasibility.',
              'ESG specialists help ensure responsible development.',
              'Legal professionals establish governance and transaction certainty.',
              'Corporate advisers prepare projects for investment.',
            ].map((line) => (
              <div key={line} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors.teal }} />
                <span className="text-base leading-relaxed" style={{ color: colors.white, opacity: 0.82 }}>{line}</span>
              </div>
            ))}
          </div>

          <blockquote className="max-w-2xl mx-auto text-center px-6 py-8 rounded-2xl" style={{ background: `linear-gradient(180deg, ${colors.teal}0F 0%, rgba(255,255,255,0.01) 100%)`, border: `1px solid ${colors.teal}26` }}>
            <p className="text-xl sm:text-2xl font-semibold leading-snug tracking-tight" style={{ color: colors.white }}>
              “Together, these disciplines create the confidence that enables institutional capital to participate.”
            </p>
          </blockquote>

          <p className="mt-10 max-w-2xl mx-auto text-center text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
            Arizore forms part of this wider ecosystem — helping bridge the pathway between{' '}
            <span style={{ color: colors.teal, fontWeight: 500 }}>verified resources and structured reserve-backed funding</span>
            {' '}while respecting the expertise that makes those opportunities possible.
          </p>
        </div>
      </section>

      {/* §1b EXPERTISE CREATES CONFIDENCE — editorial band */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 60% at 50% 40%, rgba(0,128,128,0.08) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Expertise Creates Confidence"
            title="FUNDING BEGINS WITH CONFIDENCE."
            sub="Institutional funding begins long before capital is deployed. It begins with confidence — in the data, the engineering, the ownership, the governance, and in the professionals responsible for verifying each stage of a project’s development."
          />
          <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3 mb-12">
            {['Geological data', 'Engineering', 'Ownership', 'Governance', 'Independent verification'].map((c) => (
              <span key={c} className="px-4 py-2 rounded-full text-xs sm:text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${colors.teal}30`, color: colors.white, opacity: 0.9 }}>
                {c}
              </span>
            ))}
          </div>
          <p className="max-w-2xl mx-auto text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.75 }}>
            Arizore recognises that these professionals are not supporting participants within the ecosystem —{' '}
            <span style={{ color: colors.teal, fontWeight: 500 }}>they are fundamental to it</span>. Their expertise establishes the certainty upon which responsible funding decisions are made.
          </p>
        </div>
      </section>

      {/* §2 THE EXPERTISE BEHIND EVERY PROJECT — interactive cards */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Disciplines Involved"
            title="THE EXPERTISE BEHIND EVERY PROJECT"
            sub="A range of specialist disciplines contribute throughout a project's lifecycle. Select any discipline to explore its role and how Arizore works alongside it."
          />

          <div className="space-y-3 sm:space-y-4">
            {professionals.map(({ id, Icon, title, role, support, arizore, value, engagements }) => {
              const isOpen = openCard === id;
              return (
                <div
                  key={id}
                  data-testid={`ip-card-${id}`}
                  className="rounded-2xl overflow-hidden transition-all duration-300"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${isOpen ? `${colors.teal}40` : 'rgba(255,255,255,0.08)'}` }}
                >
                  <button
                    type="button"
                    data-testid={`ip-card-toggle-${id}`}
                    aria-expanded={isOpen}
                    onClick={() => setOpenCard(isOpen ? null : id)}
                    className="w-full flex items-center gap-4 sm:gap-5 px-5 sm:px-7 py-5 sm:py-6 text-left transition-colors duration-200"
                  >
                    <span className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                      <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                    </span>
                    <span className="flex-grow text-lg sm:text-xl font-semibold tracking-tight" style={{ color: colors.white }}>{title}</span>
                    <span className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-transform duration-300" style={{ backgroundColor: isOpen ? colors.teal : 'rgba(255,255,255,0.06)', border: `1px solid ${isOpen ? colors.teal : 'rgba(255,255,255,0.15)'}` }}>
                      {isOpen ? <Minus className="w-4 h-4" style={{ color: colors.white }} /> : <Plus className="w-4 h-4" style={{ color: colors.white }} />}
                    </span>
                  </button>

                  <div className="grid transition-all duration-300 ease-out" style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}>
                    <div className="overflow-hidden">
                      <div className="px-5 sm:px-7 pb-7 pt-1 sm:pl-[6.5rem]">
                        <div className="grid gap-5 sm:grid-cols-2">
                          <Detail label="Their Role" body={role} />
                          <Detail label="How They Support Mining Projects" body={support} />
                          <Detail label="How Arizore Works Alongside Them" body={arizore} accent />
                          <Detail label="Value Created" body={value} />
                        </div>
                        <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: colors.teal, opacity: 0.85 }}>Typical Engagements</span>
                          <p className="mt-1 text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.6 }}>{engagements}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* §3 COLLABORATION, NOT REPLACEMENT */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Working Alongside Professionals"
            title="COLLABORATION, NOT REPLACEMENT."
            sub="Arizore has been designed to complement the work already being performed throughout the mining industry. Our objective is not to replace technical advisers, consultants or professional service providers."
          />
          <div className="grid gap-5 lg:gap-6 md:grid-cols-2">
            <div className="p-7 sm:p-9 rounded-2xl h-full" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] mb-5" style={{ color: colors.white, opacity: 0.55 }}>Arizore does not replace</h3>
              <ul className="space-y-3">
                {['Technical advisers', 'Geological & engineering consultants', 'Legal professionals', 'Corporate advisers', 'Professional service providers'].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-base" style={{ color: colors.white, opacity: 0.8 }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.35)' }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-7 sm:p-9 rounded-2xl h-full flex flex-col justify-center" style={{ background: `linear-gradient(180deg, ${colors.teal}12 0%, rgba(255,255,255,0.01) 100%)`, border: `1px solid ${colors.teal}33` }}>
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] mb-5" style={{ color: colors.teal }}>Arizore extends their work</h3>
              <p className="text-base sm:text-lg leading-relaxed mb-4" style={{ color: colors.white, opacity: 0.82 }}>
                Our role begins once their expertise has created the confidence required for institutional funding discussions — connecting verified resources with institutional capital through a structured reserve-backed funding model, without changing the independence or integrity of that advice.
              </p>
              <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>
                This collaborative approach enables every participant to remain focused on their area of expertise while contributing to stronger outcomes for resource owners, investors and the wider mining industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* §4 THE CONNECTED ECOSYSTEM — relationship map */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="A Connected Professional Ecosystem"
            title="ARIZORE EXISTS WITHIN THE ECOSYSTEM — NOT ABOVE IT."
            sub="These disciplines do not operate independently. They form an interconnected ecosystem that transforms exploration potential into investable opportunities. Arizore’s role is to connect these established foundations with institutional funding pathways."
          />

          {/* Radial relationship map — desktop (square for precise symmetric geometry) */}
          <div className="hidden md:block relative w-full max-w-xl mx-auto" style={{ aspectRatio: '1 / 1' }} data-testid="ip-ecosystem-map">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                {ecoNodes.map((n, i) => (
                  <linearGradient key={i} id={`ip-ln-${i}`} gradientUnits="userSpaceOnUse" x1="50" y1="50" x2={n.left} y2={n.top}>
                    <stop offset="0%" stopColor={colors.teal} stopOpacity="0.75" />
                    <stop offset="100%" stopColor={colors.teal} stopOpacity="0.12" />
                  </linearGradient>
                ))}
              </defs>
              {ecoNodes.map((n, i) => {
                const active = hoveredNode === n.label;
                const dim = hoveredNode && !active;
                return (
                  <g key={n.label}>
                    <line
                      x1="50" y1="50" x2={n.left} y2={n.top}
                      stroke={`url(#ip-ln-${i})`}
                      strokeWidth={active ? 0.55 : 0.4}
                      strokeOpacity={dim ? 0.25 : active ? 1 : 0.85}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-opacity 0.3s, stroke-width 0.3s' }}
                    />
                    {/* subtle outward pulse from Arizore */}
                    <circle r="0.65" fill={colors.teal} opacity="0">
                      <animate attributeName="cx" from="50" to={n.left} dur="5s" begin={`${i * 0.9}s`} repeatCount="indefinite" />
                      <animate attributeName="cy" from="50" to={n.top} dur="5s" begin={`${i * 0.9}s`} repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0;0.6;0" keyTimes="0;0.3;1" dur="5s" begin={`${i * 0.9}s`} repeatCount="indefinite" />
                    </circle>
                  </g>
                );
              })}
            </svg>

            {/* Peer nodes */}
            {ecoNodes.map((n) => {
              const active = hoveredNode === n.label;
              const dim = hoveredNode && !active;
              return (
                <button
                  key={n.label}
                  type="button"
                  data-testid={`ip-eco-node-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onMouseEnter={() => setHoveredNode(n.label)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onFocus={() => setHoveredNode(n.label)}
                  onBlur={() => setHoveredNode(null)}
                  className="absolute z-10 flex items-center justify-center w-[30%] h-[13%] px-4 rounded-xl text-center transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#040404] focus-visible:ring-[#008080]"
                  style={{
                    top: `${n.top}%`, left: `${n.left}%`,
                    transform: `translate(-50%, -50%) scale(${active ? 1.05 : 1})`,
                    background: active ? `linear-gradient(180deg, ${colors.teal}22 0%, rgba(10,12,14,0.96) 100%)` : 'linear-gradient(180deg, rgba(22,24,26,0.96) 0%, rgba(10,12,14,0.96) 100%)',
                    border: `1px solid ${active ? colors.teal : 'rgba(255,255,255,0.14)'}`,
                    opacity: dim ? 0.45 : 1,
                    boxShadow: active ? `0 10px 30px -12px ${colors.teal}80` : '0 8px 24px -14px rgba(0,0,0,0.8)',
                  }}
                >
                  <span className="text-xs lg:text-sm font-medium leading-tight" style={{ color: active ? colors.teal : colors.white, opacity: active ? 1 : 0.88 }}>{n.label}</span>
                </button>
              );
            })}

            {/* Center — Arizore (Ecosystem Connector) */}
            <div
              className="absolute z-20 flex flex-col items-center justify-center rounded-full text-center transition-all duration-300"
              style={{
                top: '50%', left: '50%', width: '29%', aspectRatio: '1', transform: 'translate(-50%, -50%)',
                background: `radial-gradient(circle, ${colors.teal}33 0%, rgba(6,10,11,0.97) 72%)`,
                border: `1px solid ${colors.teal}`,
                boxShadow: hoveredNode ? `0 0 55px ${colors.teal}55, inset 0 0 26px ${colors.teal}22` : `0 0 40px ${colors.teal}3D, inset 0 0 22px ${colors.teal}1A`,
              }}
            >
              <img src="/arizore-token-diagram.webp" alt="Arizore" className="w-[42%] max-w-[80px] mb-2" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <span className="text-xs lg:text-sm font-bold uppercase tracking-[0.2em]" style={{ color: colors.white }}>Arizore</span>
              <span className="mt-0.5 text-[10px] lg:text-[11px] uppercase tracking-[0.14em]" style={{ color: colors.teal }}>Ecosystem Connector</span>
            </div>
          </div>

          {/* Desktop hover caption */}
          <div className="hidden md:flex justify-center mt-10" data-testid="ip-eco-caption">
            <p className="max-w-xl text-center text-sm sm:text-base leading-relaxed transition-opacity duration-300" style={{ color: colors.white, opacity: hoveredNode ? 0.85 : 0.5 }}>
              {hoveredNode ? ecoNodes.find((n) => n.label === hoveredNode)?.desc : 'Explore how each participant contributes to the connected funding ecosystem.'}
            </p>
          </div>

          {/* Stacked — mobile */}
          <div className="md:hidden space-y-3" data-testid="ip-ecosystem-map-mobile">
            <div className="p-5 rounded-xl text-center" style={{ background: `${colors.teal}14`, border: `1px solid ${colors.teal}` }}>
              <span className="block text-sm font-bold uppercase tracking-[0.18em]" style={{ color: colors.white }}>Arizore</span>
              <span className="block text-[11px] uppercase tracking-[0.14em] mt-0.5" style={{ color: colors.teal }}>Ecosystem Connector</span>
            </div>
            {ecoNodes.map((n) => (
              <div key={n.label} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="block text-sm font-semibold mb-1" style={{ color: colors.white }}>{n.label}</span>
                <span className="block text-xs leading-relaxed" style={{ color: colors.white, opacity: 0.6 }}>{n.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §5 BUILDING BETTER OUTCOMES TOGETHER */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Building Better Outcomes Together"
            title="COLLABORATION CREATES STRONGER OUTCOMES."
            sub="The strongest reserve-backed funding solutions are created when technical excellence, professional integrity and institutional capital work together. Every participant contributes a distinct capability — and when those capabilities are connected through trust, stronger outcomes become possible."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-12">
            {pillars.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`ip-pillar-${title.toLowerCase()}`}
                className="p-7 rounded-2xl h-full flex flex-col transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-2 tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
          <p className="max-w-2xl mx-auto text-center text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
            Meaningful progress is rarely achieved in isolation. It is created through{' '}
            <span style={{ color: colors.teal, fontWeight: 500 }}>collaboration, integrity and a shared commitment to responsible resource development</span>
            {' '}— and Arizore is proud to work alongside the professionals who make those outcomes possible.
          </p>
        </div>
      </section>

      {/* §6 START A CONVERSATION */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(55% 60% at 50% 50%, rgba(0,128,128,0.1) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Start A Conversation"
            title="WORKING ALONGSIDE THE EXPERTS."
            sub="If you’re an industry professional supporting mining and resource projects, we’d welcome the opportunity to explore how Arizore can complement your work within the broader reserve-backed funding ecosystem."
          />
          <p className="max-w-2xl mx-auto -mt-4 mb-10 text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
            Whether your expertise lies in geology, engineering, advisory services, governance or strategic industry relationships, we believe collaboration creates stronger outcomes. Together, we can help connect verified resources with the capital required to unlock their long-term potential.
          </p>
          <div className="flex justify-center">
            <button
              onClick={openFilloutPopup}
              data-testid="ip-contact-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Start a Conversation
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const Detail = ({ label, body, accent }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: accent ? colors.teal : 'rgba(255,255,255,0.5)' }}>{label}</span>
    <p className="mt-1.5 text-sm sm:text-[15px] leading-relaxed" style={{ color: colors.white, opacity: accent ? 0.82 : 0.68 }}>{body}</p>
  </div>
);

export default IndustryProfessionalsPage;
